import { constants } from '../../constants/common'
import { 
  Step1Data, 
  Step2Data, 
  ComputedResults, 
  MealTypes, 
  APIPatientProfile,
  TransformedDietPlan,
  DietFormData
} from './types'

// Activity level mapping functions
export const mapActivityLevelToForm = (level: string): "sedentary" | "light" | "moderate" | "heavy" => {
  switch (level) {
    case "low": return "sedentary"
    case "moderate": return "moderate" 
    case "active": 
    case "very_active": return "heavy"
    case "light": return "light"
    default: return "sedentary"
  }
}

export const mapActivityLevelToAPI = (level: string): string => {
  switch (level) {
    case "sedentary": return "low"
    case "light": return "light"
    case "moderate": return "moderate"
    case "heavy": return "active"
    default: return "low"
  }
}

// Meal types mapping functions
export const mapMealTypesToForm = (mealTypeAvailable: Record<string, any>): MealTypes => {
  return {
    once_awake: mealTypeAvailable["Once_awake"] === 1 || mealTypeAvailable["Once awake"] === 1,
    early_morning: mealTypeAvailable["Early_Morning"] === 1 || mealTypeAvailable["Early Morning"] === 1,
    breakfast: mealTypeAvailable.Breakfast === 1,
    morning_snack: mealTypeAvailable["Morning_Snack"] === 1 || mealTypeAvailable["Morning Snack"] === 1 || mealTypeAvailable["Mid Morning"] === 1,
    lunch: mealTypeAvailable.Lunch === 1,
    evening_snack: mealTypeAvailable["Evening_Snack"] === 1 || mealTypeAvailable["Evening Snack"] === 1,
    dinner: mealTypeAvailable.Dinner === 1,
    post_dinner: mealTypeAvailable["Post_Dinner"] === 1 || mealTypeAvailable["Post Dinner"] === 1,
    bed_time: mealTypeAvailable["Bed_time"] === 1 || mealTypeAvailable["Bed time"] === 1
  }
}

export const mapMealTypesToAPI = (mealTypes: MealTypes): Record<string, number> => {
  return {
    "Once_awake": mealTypes.once_awake ? 1 : 0,
    // "Early_Morning": mealTypes.early_morning ? 1 : 0,
    "Breakfast": mealTypes.breakfast ? 1 : 0,
    "Morning_Snack": mealTypes.morning_snack ? 1 : 0,
    "Lunch": mealTypes.lunch ? 1 : 0,
    "Evening_Snack": mealTypes.evening_snack ? 1 : 0,
    "Dinner": mealTypes.dinner ? 1 : 0,
    // "Post_Dinner": mealTypes.post_dinner ? 1 : 0,
    "Bed_time": mealTypes.bed_time ? 1 : 0
  }
}

// Meal calories mapping functions
export const mapMealCaloriesToForm = (perMealKcal: Record<string, any>): Record<string, number> => {
  return {
    once_awake: perMealKcal["Once_awake"] || perMealKcal["Once awake"] || 0,
    early_morning: perMealKcal["Early_Morning"] || perMealKcal["Early Morning"] || 0,
    breakfast: perMealKcal.Breakfast || 0,
    morning_snack: perMealKcal["Morning_Snack"] || perMealKcal["Morning Snack"] || perMealKcal["Mid Morning"] || 0,
    lunch: perMealKcal.Lunch || 0,
    evening_snack: perMealKcal["Evening_Snack"] || perMealKcal["Evening Snack"] || 0,
    dinner: perMealKcal.Dinner || 0,
    post_dinner: perMealKcal["Post_Dinner"] || perMealKcal["Post Dinner"] || 0,
    bed_time: perMealKcal["Bed_time"] || perMealKcal["Bed time"] || 0
  }
}

export const mapMealCaloriesToAPI = (perMealKcal: Record<string, number>): Record<string, number> => {
  return {
    "Once_awake": perMealKcal.once_awake || 0,
    "Early_Morning": perMealKcal.early_morning || 0,
    "Breakfast": perMealKcal.breakfast || 0,
    "Morning_Snack": perMealKcal.morning_snack || 0,
    "Lunch": perMealKcal.lunch || 0,
    "Evening_Snack": perMealKcal.evening_snack || 0,
    "Dinner": perMealKcal.dinner || 0,
    "Post_Dinner": perMealKcal.post_dinner || 0,
    "Bed_time": perMealKcal.bed_time || 0
  }
}

// Diet type mapping
export const mapDietTypeToAPI = (dietType: string): string => {
  switch (dietType) {
    case "vegetarian": return "vegetarian"
    case "non_vegetarian": return "non-vegetarian"
    case "vegan": return "vegan"
    case "eggetarian": return "eggetarian"
    default: return "non-vegetarian"
  }
}

// Data transformation functions
export const transformFormDataToAPIFormat = (
  step1Data: Step1Data, 
  step2Data: Step2Data, 
  computedResults: ComputedResults,
  patientId: string,
  selectedAssessment: string
): APIPatientProfile => {
  return {
    patient_id: patientId,
    demographics: {
      gender: step1Data.demographics.gender,
      age: step1Data.demographics.age,
      height_cm: step1Data.demographics.height_cm,
      weight_kg: step1Data.demographics.weight_kg
    },
    conditions: step1Data.conditions || [],
    goal: {
      type: step1Data.goal.type,
      goal_calories: computedResults.total_calorie_goal,
      macro_split_pct: step1Data.goal.macro_split_pct,
      macro_targets_g: computedResults.macro_targets_g
    },
    diet_type: mapDietTypeToAPI(step2Data.diet_type),
    cuisine_pref: step2Data.cuisine_pref || [],
    constraints: {
      dislikes: step2Data.constraints?.dislikes || [],
      likes: step2Data.constraints?.likes || [],
      avoid_categories: step2Data.constraints?.avoid_categories || [],
      notes: step2Data.constraints?.notes || []
    },
    behavior_signals: {
      late_sleeper: step1Data.behavior_signals.late_sleeper,
      irregular_breakfast: step1Data.behavior_signals.irregular_breakfast,
      evening_tea_snack: step1Data.behavior_signals.evening_tea_snack,
      late_night_hunger: step1Data.behavior_signals.late_night_hunger,
      fruit_inconsistent: step1Data.behavior_signals.fruit_inconsistent,
      sleep_disturbed: step1Data.behavior_signals.sleep_disturbed,
      activity_level: mapActivityLevelToAPI(step1Data.behavior_signals.activity_level)
    },
    meal_type_available: mapMealTypesToAPI(step1Data.meal_types),
    meal_type_reasons: computedResults.meal_type_reasons || {},
    per_meal_kcal: mapMealCaloriesToAPI(computedResults.per_meal_kcal),
    patient_medical_test_information: {
      concerning_parameters: step2Data.medical_info.concerning_parameters || [],
      dietary_restrictions: step2Data.medical_info.dietary_restrictions || [],
      medical_considerations: step2Data.medical_info.medical_considerations || []
    },
    assessment_id: parseInt(selectedAssessment)
  }
}

export const transformAIDietPlanToAddDietPlanFormat = (aiDietPlanData: any): TransformedDietPlan | null => {
  if (!aiDietPlanData || !aiDietPlanData.personalized_diet_plan) {
    return null
  }

  const { personalized_diet_plan, patient_profile_v0 } = aiDietPlanData
  
  // Transform meals data
  const transformedMeals = (personalized_diet_plan.meals_data || []).map((meal: any, mealIndex: number) => ({
    meal_types_id: meal.meal_types_id,
    meal_name: meal.meal_name,
    start_time: meal.start_time,
    end_time: meal.end_time,
    order_no: meal.order_no,
    is_hidden: "N",
    patient_permission: "R",
    options: (meal.options || []).map((option: any, optionIndex: number) => ({
      options_name: `Option ${optionIndex + 1}`,
      order_no: option.option_no,
      tips: option.tips,
      total_calories: option.meal_total_calories?.toString(),
      total_carbs: option.meal_total_carbs?.toString(),
      total_proteins: option.meal_total_protein?.toString(),
      total_fats: option.meal_total_fats?.toString(),
      total_fibers: option.meal_total_fiber?.toString(),
      total_sodium: option.meal_total_sodium?.toString(),
      total_potassium: option.meal_total_potassium?.toString(),
      total_sugar: option.meal_total_sugar?.toString(),
      total_fatty_acids: option.meal_total_fatty_acid?.toString(),
      food_items: (option.food_items || []).map((foodItem: any) => {
        const qty = parseFloat(foodItem.quantity) || 1; // default to 1 if quantity is missing/invalid
        return {
          food_item_id: foodItem.food_item_id,
          food_item_name: foodItem.food_name,
          quantity: foodItem.quantity,
          measure_id: foodItem.unit_option_id,
          measure_name: foodItem.unit_option_name,
          protein: (foodItem.protein / qty).toString(),
          carbs: (foodItem.carbs / qty).toString(),
          fats: (foodItem.fats / qty).toString(),
          fibers: (foodItem.fiber / qty).toString(),
          calories: (foodItem.calories / qty).toString(),
          sodium: (foodItem.sodium / qty).toString(),
          potassium: (foodItem.potassium / qty).toString(),
          sugar: (foodItem.sugar / qty).toString(),
          fatty_acids: (foodItem.fatty_acid / qty).toString(),
          order_no: foodItem.order_no,
          is_deleted: "N",
        };
      }),
    })),
  }))

  // Create the transformed diet plan data
  return {
    plan_name: `AI Generated Diet Plan - ${aiDietPlanData.diet_plan}`,
    patient_id: patient_profile_v0.patient_id,
    start_date: new Date().toISOString().split('T')[0], // Today's date
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from today
    total_calories: personalized_diet_plan.total_calories?.toString(),
    actual_calories: personalized_diet_plan.actual_calories?.toString(),
    protein: personalized_diet_plan.proteins?.toString(),
    fat: personalized_diet_plan.fat?.toString(),
    carbs: personalized_diet_plan.carbs?.toString(),
    status: "Draft",
    diet_plan_meal_rel: transformedMeals,
  }
}

// Helper functions
export const getDietPlanOptions = (dietPlanData: any) => {
  const apiDietPlanOptions = dietPlanData?.top_5_regenerated_diet_plans?.map((plan: any) => ({
    value: plan.generation_id,
    label: `${plan.diet_plan} (${plan.status})`
  })) || []

  return [
    ...apiDietPlanOptions,
    {
      value: "default",
      label: "Default"
    }
  ]
}

export const getSelectedDietPlanStatus = (selectedDropdownOption: string, dietPlanData: any): string | null => {
  if (!selectedDropdownOption) {
    return null
  }
  
  if (selectedDropdownOption === constants.DEFAULT) {
    if (dietPlanData?.diet_plan?.personalized_diet_plan || 
        dietPlanData?.top_5_regenerated_diet_plans?.length > 0) {
      return constants.SUCCESS
    }
    return null
  }
  
  if (!dietPlanData?.top_5_regenerated_diet_plans) {
    return null
  }
  
  const selectedPlan = dietPlanData.top_5_regenerated_diet_plans.find(
    (plan: any) => plan.generation_id === selectedDropdownOption
  )
  
  return selectedPlan?.status || null
}

export const getPatientProfile = (selectedDropdownOption: string, dietPlanData: any) => {
  let profile = null
  
  if (selectedDropdownOption === constants.DEFAULT) {
    if (dietPlanData.diet_plan?.patient_profile_v0) {
      profile = dietPlanData.diet_plan.patient_profile_v0
    } else if (dietPlanData.top_5_regenerated_diet_plans?.[0]?.patient_profile_v0) {
      profile = dietPlanData.top_5_regenerated_diet_plans[0].patient_profile_v0
    }
  } else {
    const selectedPlan = dietPlanData.top_5_regenerated_diet_plans?.find(
      (plan: any) => plan.generation_id === selectedDropdownOption
    )
    if (selectedPlan?.patient_profile_v0) {
      profile = selectedPlan.patient_profile_v0
    }
  }
  
  return profile
}

// Initialize form data with defaults and API data if available
export const initializeFormData = (apiData?: any, selectedDropdownOption?: string): DietFormData => {
  // Default values
  const defaultFormData: DietFormData = {
    // Demographics
    demographics: {
      gender: "M",
      age: 25,
      height_cm: 170,
      weight_kg: 70
    },
    conditions: [],
    goal: {
      type: "maintain",
      macro_split_pct: { carbs: 50, protein: 25, fats: 25 }
    },
    behavior_signals: {
      late_sleeper: false,
      irregular_breakfast: false,
      evening_tea_snack: false,
      late_night_hunger: false,
      fruit_inconsistent: false,
      sleep_disturbed: false,
      activity_level: "moderate"
    },
    meal_types: {
      once_awake: false,
      early_morning: false,
      breakfast: true,
      morning_snack: false,
      lunch: true,
      evening_snack: false,
      dinner: true,
      post_dinner: false,
      bed_time: false
    },
    // Preferences & Medical
    diet_type: "vegetarian",
    cuisine_pref: [],
    constraints: {
      dislikes: [],
      likes: [],
      avoid_categories: [],
      notes: []
    },
    medical_info: {
      concerning_parameters: [],
      dietary_restrictions: [],
      medical_considerations: []
    },
    // Computed Results
    total_calorie_goal: 2000,
    macro_targets_g: {
      carbs: 250,
      protein: 125,
      fats: 67
    },
    meal_type_reasons: {},
    per_meal_kcal: {}
  }

  // If no API data, return defaults
  if (!apiData) {
    return defaultFormData
  }

  // Extract profile from API data based on selected dropdown option
  let profile = null

  if (selectedDropdownOption && selectedDropdownOption !== constants.DEFAULT) {
    // Use the selected diet plan's profile
    const selectedPlan = apiData?.top_5_regenerated_diet_plans?.find(
      (plan: any) => plan.generation_id === selectedDropdownOption
    )
    if (selectedPlan?.patient_profile_v0 && selectedPlan.status === constants.SUCCESS) {
      profile = selectedPlan.patient_profile_v0
    }
  } else {
    // Use default profile (first available)
    if (apiData?.diet_plan?.patient_profile_v0) {
      profile = apiData.diet_plan.patient_profile_v0
    } else if (apiData?.top_5_regenerated_diet_plans?.[0]?.patient_profile_v0) {
      profile = apiData.top_5_regenerated_diet_plans[0].patient_profile_v0
    } else if (apiData?.patient_profile_v0) {
      profile = apiData.patient_profile_v0
    }
  }

  if (!profile) {
    return defaultFormData
  }

  // Map API data to form data
  return {
    demographics: {
      gender: profile.demographics?.gender || defaultFormData.demographics.gender,
      age: profile.demographics?.age || defaultFormData.demographics.age,
      height_cm: profile.demographics?.height_cm || defaultFormData.demographics.height_cm,
      weight_kg: profile.demographics?.weight_kg || defaultFormData.demographics.weight_kg
    },
    conditions: profile.conditions || defaultFormData.conditions,
    goal: {
      type: profile.goal?.type || defaultFormData.goal.type,
      macro_split_pct: profile.goal?.macro_split_pct || defaultFormData.goal.macro_split_pct,
      goal_calories: profile.goal?.goal_calories,
      macro_targets_g: profile.goal?.macro_targets_g
    },
    behavior_signals: {
      late_sleeper: profile.behavior_signals?.late_sleeper || defaultFormData.behavior_signals.late_sleeper,
      irregular_breakfast: profile.behavior_signals?.irregular_breakfast || defaultFormData.behavior_signals.irregular_breakfast,
      evening_tea_snack: profile.behavior_signals?.evening_tea_snack || defaultFormData.behavior_signals.evening_tea_snack,
      late_night_hunger: profile.behavior_signals?.late_night_hunger || defaultFormData.behavior_signals.late_night_hunger,
      fruit_inconsistent: profile.behavior_signals?.fruit_inconsistent || defaultFormData.behavior_signals.fruit_inconsistent,
      sleep_disturbed: profile.behavior_signals?.sleep_disturbed || defaultFormData.behavior_signals.sleep_disturbed,
      activity_level: mapActivityLevelToForm(profile.behavior_signals?.activity_level) || defaultFormData.behavior_signals.activity_level
    },
    meal_types: {
      ...defaultFormData.meal_types,
      ...mapMealTypesToForm(profile.meal_type_available)
    },
    diet_type: profile.diet_type || defaultFormData.diet_type,
    cuisine_pref: profile.cuisine_pref || defaultFormData.cuisine_pref,
    constraints: {
      dislikes: profile.constraints?.dislikes || defaultFormData.constraints.dislikes,
      likes: profile.constraints?.likes || defaultFormData.constraints.likes,
      avoid_categories: profile.constraints?.avoid_categories || defaultFormData.constraints.avoid_categories,
      notes: profile.constraints?.notes || defaultFormData.constraints.notes
    },
    medical_info: {
      concerning_parameters: profile.patient_medical_test_information?.concerning_parameters || defaultFormData.medical_info.concerning_parameters,
      dietary_restrictions: profile.patient_medical_test_information?.dietary_restrictions || defaultFormData.medical_info.dietary_restrictions,
      medical_considerations: profile.patient_medical_test_information?.medical_considerations || defaultFormData.medical_info.medical_considerations
    },
    total_calorie_goal: profile.goal?.goal_calories || defaultFormData.total_calorie_goal,
    macro_targets_g: profile.goal?.macro_targets_g || defaultFormData.macro_targets_g,
    meal_type_reasons: profile.meal_type_reasons || defaultFormData.meal_type_reasons,
    per_meal_kcal: mapMealCaloriesToForm(profile.per_meal_kcal) || defaultFormData.per_meal_kcal
  }
}

// Transform unified form data to API format
export const transformUnifiedFormDataToAPIFormat = (
  formData: DietFormData,
  patientId: string,
  selectedAssessment: string
): APIPatientProfile => {
  return {
    patient_id: patientId,
    demographics: {
      gender: formData.demographics.gender,
      age: formData.demographics.age,
      height_cm: formData.demographics.height_cm,
      weight_kg: formData.demographics.weight_kg
    },
    conditions: formData.conditions || [],
    goal: {
      type: formData.goal.type,
      goal_calories: formData.total_calorie_goal,
      macro_split_pct: formData.goal.macro_split_pct,
      macro_targets_g: formData.macro_targets_g
    },
    diet_type: mapDietTypeToAPI(formData.diet_type),
    cuisine_pref: formData.cuisine_pref || [],
    constraints: {
      dislikes: formData.constraints?.dislikes || [],
      likes: formData.constraints?.likes || [],
      avoid_categories: formData.constraints?.avoid_categories || [],
      notes: formData.constraints?.notes || []
    },
    behavior_signals: {
      late_sleeper: formData.behavior_signals.late_sleeper,
      irregular_breakfast: formData.behavior_signals.irregular_breakfast,
      evening_tea_snack: formData.behavior_signals.evening_tea_snack,
      late_night_hunger: formData.behavior_signals.late_night_hunger,
      fruit_inconsistent: formData.behavior_signals.fruit_inconsistent,
      sleep_disturbed: formData.behavior_signals.sleep_disturbed,
      activity_level: mapActivityLevelToAPI(formData.behavior_signals.activity_level)
    },
    meal_type_available: mapMealTypesToAPI(formData.meal_types),
    meal_type_reasons: formData.meal_type_reasons || {},
    per_meal_kcal: mapMealCaloriesToAPI(formData.per_meal_kcal),
    patient_medical_test_information: {
      concerning_parameters: formData.medical_info.concerning_parameters || [],
      dietary_restrictions: formData.medical_info.dietary_restrictions || [],
      medical_considerations: formData.medical_info.medical_considerations || []
    },
    assessment_id: parseInt(selectedAssessment)
  }
}